import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import useTour from "./TourContext";
import "./tour.css";

const PAD = 8;
const GAP = 14;
const MARGIN = 16;
const TOOLTIP_WIDTH = 320;

export default function TourOverlay() {
  const location = useLocation();
  const { active, stepIndex, totalSteps, isFirst, isLast, currentStep, next, back, skip } = useTour();
  const [rect, setRect] = useState(null);
  const tooltipRef = useRef(null);
  const [tooltipH, setTooltipH] = useState(220);

  const onThisPage = active && currentStep && currentStep.route === location.pathname;

  useEffect(() => {
    if (!onThisPage) {
      setRect(null);
      return undefined;
    }

    let cancelled = false;
    let retryId;

    const measure = () => {
      const el = document.querySelector(currentStep.selector);
      if (!el) {
        // Content the step targets can render a beat late (data still loading) — keep
        // trying briefly instead of giving up on the first miss.
        retryId = setTimeout(measure, 200);
        return;
      }
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      const update = () => {
        if (cancelled) return;
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      };
      update();
      window.addEventListener("resize", update);
      window.addEventListener("scroll", update, true);
      retryId = () => {
        window.removeEventListener("resize", update);
        window.removeEventListener("scroll", update, true);
      };
    };

    const kickoff = setTimeout(measure, 50);

    return () => {
      cancelled = true;
      clearTimeout(kickoff);
      if (typeof retryId === "function") retryId();
      else clearTimeout(retryId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onThisPage, currentStep?.selector, stepIndex]);

  // Measure the tooltip's own rendered height so "place above" can be clamped
  // correctly instead of guessing — a wrong guess previously pushed it off-screen.
  useLayoutEffect(() => {
    if (!tooltipRef.current) return;
    const h = tooltipRef.current.getBoundingClientRect().height;
    // Intentionally runs after every render (no deps) — it only calls setState when the
    // measured height actually changed, so this settles in one extra render, not a loop.
    if (h > 0 && Math.abs(h - tooltipH) > 1) setTooltipH(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  if (!onThisPage) return null;

  const spotlightStyle = rect
    ? {
        top: rect.top - PAD,
        left: rect.left - PAD,
        width: rect.width + PAD * 2,
        height: rect.height + PAD * 2,
      }
    : null;

  const viewportH = window.innerHeight;
  const viewportW = window.innerWidth;

  let tooltipPositionClass = "is-centered";
  let tooltipStyle;
  if (rect) {
    const spaceBelow = viewportH - (rect.top + rect.height + PAD);
    const spaceAbove = rect.top - PAD;
    const left = Math.max(MARGIN, Math.min(rect.left, viewportW - TOOLTIP_WIDTH - MARGIN));

    let top;
    if (spaceBelow >= tooltipH + GAP || spaceBelow >= spaceAbove) {
      tooltipPositionClass = "is-below";
      top = Math.min(rect.top + rect.height + PAD + GAP, viewportH - tooltipH - MARGIN);
    } else {
      tooltipPositionClass = "is-above";
      top = rect.top - PAD - GAP - tooltipH;
    }
    top = Math.max(MARGIN, top);
    tooltipStyle = { top, left };
  }

  return (
    <div className="admin-tour-layer">
      {spotlightStyle ? (
        <div className="admin-tour-spotlight" style={spotlightStyle} />
      ) : (
        <div className="admin-tour-dim" />
      )}

      <div ref={tooltipRef} className={`admin-tour-tooltip ${tooltipPositionClass}`} style={tooltipStyle}>
        <div className="admin-tour-step-count">
          Step {stepIndex + 1} of {totalSteps}
        </div>
        <h4 className="admin-tour-title">{currentStep.title}</h4>
        <p className="admin-tour-desc">{currentStep.description}</p>
        <div className="admin-tour-actions">
          <button type="button" className="admin-tour-btn admin-tour-btn-skip" onClick={skip}>
            Skip
          </button>
          <div className="admin-tour-actions-right">
            {!isFirst && (
              <button type="button" className="admin-tour-btn admin-tour-btn-back" onClick={back}>
                Back
              </button>
            )}
            <button type="button" className="admin-tour-btn admin-tour-btn-next" onClick={next}>
              {isLast ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
